import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Unlink } from 'lucide-react';
import { useState } from 'react';

import { deleteInvoiceLink, getInvoiceDownload, listAutoMatchedLinks } from '@/api/invoices';
import { Button } from '@/components/ui/button';

import { useAuth } from '../auth/AuthContext';

const PAGE_SIZE = 200;

/** The auto-match audit list: every link the batch auto-matcher wrote,
 *  newest first, with the transaction context to verify against the
 *  PDF and a one-click Unlink for misses. Collapsed by default —
 *  spot-checking is a deliberate act, not ambient noise. Renders
 *  nothing when the auto-matcher hasn't linked anything.
 */
export function AutoMatchedPanel({ slug }: { slug: string }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const links = useInfiniteQuery({
    queryKey: ['auto-matched-links', slug, userKey],
    enabled: isSignedIn,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      listAutoMatchedLinks(slug, (await getIdToken()) ?? '', {
        limit: PAGE_SIZE,
        offset: pageParam,
      }),
    getNextPageParam: (last) => last.next_offset ?? undefined,
  });

  const unlink = useMutation({
    mutationFn: async (linkId: string) =>
      deleteInvoiceLink(slug, linkId, (await getIdToken()) ?? ''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['auto-matched-links', slug] });
      void qc.invalidateQueries({ queryKey: ['invoice-pool', slug] });
      // Run pages' attachment chips read this key — an unlink here must
      // surface there without a hard refresh.
      void qc.invalidateQueries({ queryKey: ['transaction-documents', slug] });
    },
  });

  const [viewError, setViewError] = useState(false);
  const viewDocument = async (documentId: string) => {
    // Open the tab synchronously so the browser keeps the click's user
    // activation; navigate it once the signed URL resolves. `noopener`
    // would make window.open return null, so sever the opener by hand.
    const tab = window.open('', '_blank');
    if (!tab) {
      setViewError(true);
      return;
    }
    tab.opener = null;
    try {
      const download = await getInvoiceDownload(slug, documentId, (await getIdToken()) ?? '');
      setViewError(false);
      tab.location.href = download.url;
    } catch {
      tab.close();
      setViewError(true);
    }
  };

  const items = links.data?.pages.flatMap((page) => page.items) ?? [];
  const retry = (
    <button type="button" className="underline" onClick={() => void links.refetch()}>
      retry
    </button>
  );
  // A later page failing must not hide the rows already loaded — only
  // fall back to the standalone error when there is nothing to show.
  if (links.isError && items.length === 0) {
    return (
      <p role="alert" className="text-sm text-red-700">
        Auto-matched links failed to load — {retry}
      </p>
    );
  }
  if (links.isPending || items.length === 0) return null;

  return (
    <section className="grid gap-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="justify-self-start text-base font-semibold text-brand-navy hover:text-brand-royal"
      >
        {open ? '▾' : '▸'} Auto-matched ({items.length}
        {links.hasNextPage ? '+' : ''})
      </button>
      {open && (
        <div className="grid gap-1 rounded-lg border border-border bg-card p-3">
          <p className="text-xs text-brand-slate">
            Links written by the auto-matcher — spot-check a sample against the PDF and unlink
            any miss (the document returns to the queue).
          </p>
          <ul className="grid gap-1">
            {items.map((entry) => (
              <li
                key={entry.id}
                className="flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm"
              >
                <div className="grid min-w-0 flex-1 gap-0.5">
                  <span className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="truncate font-mono text-xs text-brand-slate">
                      {entry.filename}
                    </span>
                    <span className="font-mono text-xs text-brand-slate">
                      {entry.external_transaction_id}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-baseline gap-2 text-xs text-brand-slate">
                    <span className="font-medium text-brand-navy">
                      {entry.item?.supplier_name ?? '(no supplier)'}
                    </span>
                    {entry.item && (
                      <span className="font-mono">
                        {entry.item.bill_amount} {entry.item.currency}
                      </span>
                    )}
                    {entry.item?.bill_date && <span>{entry.item.bill_date}</span>}
                    <span>matched {entry.assigned_at.slice(0, 10)}</span>
                  </span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  onClick={() => void viewDocument(entry.source_document_id)}
                  title="Open the source document in a new tab"
                  className="shrink-0"
                >
                  <ExternalLink className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  View
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={unlink.isPending}
                  onClick={() => unlink.mutate(entry.id)}
                  title="Unlink — the document returns to the unassigned queue"
                  className="shrink-0"
                >
                  <Unlink className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  Unlink
                </Button>
              </li>
            ))}
          </ul>
          {links.hasNextPage && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={links.isFetchingNextPage}
              onClick={() => void links.fetchNextPage()}
              className="justify-self-start"
            >
              {links.isFetchingNextPage ? 'Loading…' : 'Load more'}
            </Button>
          )}
          {links.isFetchNextPageError && (
            <p role="alert" className="text-sm text-red-700">
              Loading more links failed — {retry}
            </p>
          )}
          {viewError && (
            <p role="alert" className="text-sm text-red-700">
              Opening the document failed — try View again.
            </p>
          )}
          {unlink.isError && (
            <p className="text-sm text-red-700">Unlink failed — retry.</p>
          )}
        </div>
      )}
    </section>
  );
}
